import React, { useEffect, useRef, useState } from "react"
import Helmet from "react-helmet"
import { Link } from "gatsby"
import { ArrowRight } from "react-feather"
import { Layout } from "../components/Layout"
import { SEO } from "../components/SEO"
import { AffiliateList } from "../components/AffiliateList"
import { Compare } from "../components/Compare"
import { Reviews } from "../components/Reviews"
import { CTABlock } from "../components/CTABlock"
import InstagramFeed from "../components/InstagramFeed"
import elizabethStreet from "../assets/images/elizabeth-street.jpg"
import elizabethTatler from "../assets/images/elizabeth-tatler.avif"
import award1 from "../assets/icons/award-1.svg"
import globalExcellenceBanner from "../assets/images/2025 -Global -Excellence-Banner.png"
import doctify from "../assets/images/doctify-logo.svg"
import award2 from "../assets/icons/award-2.svg"
import aestheticAwards from "../assets/images/aesthetic-awards.png"
import doctifyBadge from "../assets/images/UK-Great.png"
import blogPlaceholder01 from "../assets/images/polly-vernon-media-feature-02.jpg"
import blogPlaceholder02 from "../assets/images/elizabeth-blog-placeholder-01.jpg"
import bA1b from "../assets/images/b-a-1-before.jpg"
import bA1a from "../assets/images/b-a-1-after.jpg"
import bA2b from "../assets/images/b-a-2-before.jpg"
import bA2a from "../assets/images/b-a-2-after.jpg"
import bA3b from "../assets/images/b-a-3-before.jpg"
import bA3a from "../assets/images/b-a-3-after.jpg"
import profile2 from "../assets/images/elizabeth-suit.jpg"
import profile3 from "../assets/images/elizabeth-red.jpg"
import check from "../assets/icons/check.svg"
import hello from "../assets/icons/hello.svg"
import dailyMail from "../assets/icons/daily-mail.svg"
import iconTrendingFlat from "../assets/icons/icon_trending_flat_white.svg"
import glamour from "../assets/icons/glamour.svg"
import telegraph from "../assets/icons/telegraph.svg"
import bbcRadio from "../assets/icons/bbc-radio.svg"
import mayfair from "../assets/icons/mayfair.svg"
import tatler from "../assets/icons/tatler.svg"
import marksAndSpencer from "../assets/icons/marks-and-spencer-logo.svg"
import aesthetics from "../assets/images/aesthetics.webp"
import royalCollege from "../assets/images/royal-college.svg"
import signature from "../assets/images/signature.svg"
import stylist from "../assets/icons/stylist.svg"
import sundayTimes from "../assets/icons/sunday-times.svg"
import yahoo from "../assets/icons/yahoo.svg"
import buttons from "../styles/buttons.module.css"
import containerClasses from "../styles/container.module.css"
import starReview from "../assets/images/star_review.svg"

const IndexPage = () => {
  const isBrowser = () => typeof window !== "undefined"
  const parallaxRef = useRef(null)
  const [quoteVisible, setQuoteVisible] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      const el = parallaxRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const raw =
        (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
      const scrolled = Math.max(0, Math.min(1, raw))
      const img = el.querySelector(".parallax-img")
      if (img) {
        // Drift proportionally to the section height so the parallax stays
        // noticeable no matter how tall the band is. Travel DOWNWARD only:
        // that reveals the space above her head as you scroll, and never
        // shifts the image up (which would slice her head off at the top).
        // scale(1.35) leaves enough overflow that the top edge never shows.
        const shift = scrolled * rect.height * 0.28
        img.style.transform = `translateY(${shift}px) scale(1.35)`
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Reveal the quote only once it scrolls into view (otherwise the
  // fade/slide animations play on page load, far below the fold, and are
  // finished long before the user ever scrolls down to them).
  useEffect(() => {
    const el = parallaxRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setQuoteVisible(true)
            observer.disconnect()
          }
        })
      },
      { threshold: 0.25 }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const pressLogos = [
    { src: marksAndSpencer, alt: "Marks & Spencer", maxH: "max-h-5" },
    { src: tatler,        alt: "Tatler" },
    { src: hello,         alt: "Hello! Magazine" },
    { src: mayfair,       alt: "Mayfair Times" },
    { src: dailyMail,     alt: "Daily Mail" },
    { src: glamour,       alt: "Glamour" },
    { src: yahoo,         alt: "Yahoo" },
    { src: sundayTimes,   alt: "The Sunday Times" },
    { src: stylist,       alt: "Stylist" },
    { src: telegraph,     alt: "The Telegraph" },
    { src: bbcRadio,      alt: "BBC" },
  ]

  return (
    <Layout page="Home">
      <SEO
        title="Dr Hawkes Clinic | Blepharoplasty Expert, Oculoplastic Surgery & Aesthetics, London"
        description="Dr Hawkes Clinic, led by Consultant Oculoplastic Surgeon Dr Elizabeth Hawkes — blepharoplasty expert and specialist in eyelid surgery, oculoplastics and facial aesthetics. Practising at Cadogan Clinic, Chelsea and The London Lauriston Clinic."
        pathname="/"
      />
      <Helmet>
        <script>{`(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:3002941,hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`}</script>
      </Helmet>
      {/* Hero  Section */}
      <div className="relative overflow-hidden background-panel custom-hero-height fade-in-on-load hero-mobile-nav-offset">
        <div
          className={`${containerClasses.container} grid lg:grid-cols-12 lg:grid-rows-1 grid-rows-2 h-full`}
        >
          {/* <!-- First Div (50% Width) --> */}
          <div className="box-border flex flex-col items-center justify-center h-full col-span-4 row-span-1 overflow-hidden text-center pt-16 md:pt-20 lg:pt-0 lg:col-span-6 xl:col-span-6 lg:text-left md:text-center sm:justify-center md:items-center lg:items-start md:col-span-8">
            <p
              className="mt-0 mb-4 font-mono text-xs uppercase text-gold600"
              style={{ letterSpacing: "0.2em" }}
            >
              Dr Hawkes Clinic
            </p>
            <h1 className="mt-0 mb-7 heading-hero">
              Blepharoplasty Expert, Eye Surgeon, Aesthetic Doctor
            </h1>
            <p className="mb-0 max-w-xl text-base md:text-xl leading-relaxed text-center text-gray-700 md:text-center lg:text-left lg:pr-8">
              Award-winning Consultant Ophthalmic and Oculoplastic Surgeon
              and Aesthetic Doctor, specialising in blepharoplasty and
              advanced facial aesthetics in London.
            </p>
            <div className="mt-8 lg:mt-10">
              <Link
                className="hero-cta-gold group inline-flex items-center font-mono text-sm uppercase text-white px-10 py-4 transition-colors duration-300"
                style={{ letterSpacing: "0.22em" }}
                to="/contact/"
              >
                <span>Book Now</span>
                <span
                  className="ml-4 inline-block transition-transform duration-300 group-hover:translate-x-1"
                  aria-hidden="true"
                >
                  →
                </span>
              </Link>
            </div>
          </div>

          {/* <!-- Second Div (50% Width) --> */}
          <div className="flex items-center justify-center h-full col-span-4 row-span-1 lg:col-span-6 lg:col-start-7">
            {/* Content for the second half of the container */}
          </div>
        </div>
      </div>
      {/* Text Image Section */}
      <div className="bg-white">
        <div
          className={`${containerClasses.container} py-12 md:py-24 lg:py-20`}
        >
          {/* Text */}
          <div className="flex flex-col col-span-4 md:col-span-8 lg:justify-center lg:col-span-5">
            <p className="mt-0 overline">Why choose Dr Hawkes Clinic?</p>
            <h2 className="mt-0 mb-6 heading-md">
              Bespoke care, <br />
              <i>natural</i> results.
            </h2>
            <ul>
              <li>
                <div className="flex">
                  <img
                    loading="lazy"
                    className="w-6 h-6 mx-auto mr-2"
                    src={check}
                    alt="Checkmark"
                  />
                  <p>
                    <span className="font-bold">Bespoke Treatment:</span> Dr.
                    Hawkes meticulously formulates a personalised treatment plan
                    specifically tailored to your individual needs, medical
                    background, and aspirations.
                  </p>
                </div>
              </li>
              <li>
                <div className="flex">
                  <img
                    loading="lazy"
                    className="w-6 h-6 mx-auto mr-2"
                    src={check}
                    alt="Checkmark"
                  />
                  <p>
                    <span className="font-bold">Excellence in Treatment:</span>{" "}
                    With over 20 years of medical and surgical training, Dr
                    Hawkes offers expertise in eye surgery, eye care and
                    aesthetic treatments.
                  </p>
                </div>
              </li>
              <li>
                <div className="flex">
                  <img
                    loading="lazy"
                    className="w-6 h-6 mx-auto mr-2"
                    src={check}
                    alt="Checkmark"
                  />
                  <p>
                    <span className="font-bold ">Top-tier Facility:</span> Dr
                    Hawkes is the lead Oculoplastic Surgeon at the
                    internationally renowned Cadogan Clinic in Chelsea, London,
                    where your safety and comfort are paramount.
                  </p>
                </div>
              </li>
            </ul>
            <a
              href="https://www.doctify.com/uk/specialist/ms-elizabeth-dolatshany-hawkes"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center flex-wrap gap-y-1 mb-4 hover:opacity-80 transition-opacity"
              aria-label="View Dr Elizabeth Hawkes on Doctify (opens in new tab)"
            >
              <img
                loading="lazy"
                className="h-4 md:h-5 -mr-1"
                src={starReview}
                alt="Rating Star"
              />
              <img
                loading="lazy"
                className="h-4 md:h-5 -mr-1"
                src={starReview}
                alt="Rating Star"
              />
              <img
                loading="lazy"
                className="h-4 md:h-5 -mr-1"
                src={starReview}
                alt="Rating Star"
              />
              <img
                loading="lazy"
                className="h-4 md:h-5 -mr-1"
                src={starReview}
                alt="Rating Star"
              />
              <img
                loading="lazy"
                className="h-4 md:h-5 mr-2 md:mr-3"
                src={starReview}
                alt="Rating Star"
              />
              <p className="mb-0 mr-2 whitespace-no-wrap">
                <span className="font-bold">
                  100+ <span className="hidden md:inline">5-star </span>reviews
                </span>{" "}
                on
              </p>
              <img
                loading="lazy"
                className="h-5"
                src={doctify}
                alt="Doctify logo featuring a stylised blue and green apple icon next to the word 'Doctify' in black text."
              />
            </a>
          </div>
          {/* Image */}
          <div className="col-span-4 md:col-span-8 lg:col-start-7 lg:col-span-6">
            <img
              loading="lazy"
              className="w-full"
              alt="Dr Hawkes seated on a modern black chair, dressed in an elegant white suit with a ruffled blouse, on a wooden floor against a plain beige background."
              src={profile2}
            />
          </div>
        </div>
      </div>

      {/* Credentials / Recognised By */}
      <div className="bg-white2 border-t border-gray-100">
        <div className={`${containerClasses.container} py-20 md:py-28 lg:py-32`}>
          <div className="col-span-4 md:col-span-8 lg:col-span-12 text-center">
            <p
              className="mt-0 mb-8 md:mb-10 font-mono text-xs uppercase text-gold600"
              style={{ letterSpacing: "0.2em" }}
            >
              Recognised By
            </p>
            <div className="flex flex-col md:flex-row md:justify-center items-center gap-10 md:gap-16 lg:gap-24">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 md:h-14 w-36 md:w-44 mb-4">
                  <img
                    loading="lazy"
                    alt="Marks & Spencer"
                    className="max-h-6 md:max-h-7 w-auto max-w-full object-contain"
                    src={marksAndSpencer}
                  />
                </div>
                <p
                  className="mb-0 font-mono text-xs leading-none text-blackNew uppercase"
                  style={{ letterSpacing: "0.18em" }}
                >
                  M&amp;S Beauty Ambassador
                </p>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 md:h-14 w-36 md:w-44 mb-4">
                  <img
                    loading="lazy"
                    alt="Tatler"
                    className="max-h-9 md:max-h-10 w-auto max-w-full object-contain"
                    src={tatler}
                  />
                </div>
                <p
                  className="mb-0 font-mono text-xs leading-none text-blackNew uppercase"
                  style={{ letterSpacing: "0.18em" }}
                >
                  Tatler Top Doctor
                </p>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 md:h-14 w-36 md:w-44 mb-4">
                  <img
                    loading="lazy"
                    alt="Royal College of Ophthalmologists"
                    className="max-h-8 md:max-h-9 w-auto max-w-full object-contain"
                    src={royalCollege}
                  />
                </div>
                <p
                  className="mb-0 font-mono text-xs leading-none text-blackNew uppercase"
                  style={{ letterSpacing: "0.18em" }}
                >
                  RCOphth Examiner
                </p>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 md:h-14 w-36 md:w-44 mb-4">
                  <img
                    loading="lazy"
                    alt="Aesthetics Journal"
                    className="max-h-9 md:max-h-10 w-auto max-w-full object-contain"
                    src={aesthetics}
                  />
                </div>
                <p
                  className="mb-0 font-mono text-xs leading-none text-blackNew uppercase"
                  style={{ letterSpacing: "0.18em" }}
                >
                  Journal Advisor
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Homepage Quote — Parallax */}
      <div
        ref={parallaxRef}
        className="relative overflow-hidden quote-parallax"
      >
        <img
          src={elizabethStreet}
          alt=""
          className="parallax-img absolute inset-0 w-full h-full object-cover"
          style={{
            willChange: "transform",
            transform: "scale(1.35)",
            objectPosition: "center 25%",
          }}
        />
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className={`${containerClasses.container} relative z-10`}>
          <div className="col-span-4 md:col-span-8 lg:col-span-10 lg:col-start-2">
            <h2
              className={`my-0 text-center text-white text-4xl lg:py-16 md:text-6xl lg:text-7xl xl:text-8xl md:leading-tight ${
                quoteVisible ? "animate-fadeIn" : "opacity-0"
              }`}
            >
              &ldquo;I am dedicated to delivering <i>exceptional care</i>,
              tailored to my patient&rsquo;s unique needs.&rdquo;
            </h2>
            <p
              className={`mt-8 mb-0 font-mono text-center text-white ${
                quoteVisible ? "animate-slideIn" : "opacity-0"
              }`}
            >
              – Dr Elizabeth Hawkes
            </p>
          </div>
        </div>
      </div>
      {/* Before and afters */}
      <div className="py-12 border-solid bg-white2 md:py-24 lg:py-20">
        <div className={`${containerClasses.container} `}>
          <div className="col-span-4 md:col-span-8 lg:col-span-10 lg:col-start-2">
            <p className="my-0 text-center md:col-span-8 overline">
              Before & After
            </p>
            <div className=" lg:col-span-6 lg:col-start-4">
              <h2 className="mb-4 text-center heading-md">
                Real patients, <i>tangible</i> results.
              </h2>
            </div>
            <div className="grid gap-4 mt-4 md:grid-cols-3">
              <Compare imageOne={bA2b} imageTwo={bA2a} className="" />
              <Compare imageOne={bA3b} imageTwo={bA3a} className="mb-2" />
              <Compare imageOne={bA1b} imageTwo={bA1a} className="" />
            </div>
          </div>
        </div>
      </div>
      {/* Testimonials */}
      <Reviews />
      {/* Awards & Commendations */}
      <div className="py-12 bg-white md:py-20 lg:py-24">
        <div className={`${containerClasses.container}`}>
          <div className="col-span-4 md:col-span-8 lg:col-span-12 text-center mb-10 lg:mb-14">
            <p className="my-0 overline">Recognition</p>
            <h2 className="mt-0 mb-0 heading-md">
              Awards & <i>Commendations</i>
            </h2>
          </div>
          <div className="col-span-4 md:col-span-8 lg:col-span-8 lg:col-start-3">
            {/* GHP – Best Oculoplastic Surgeon 2026, London */}
            <div className="flex items-center py-5 border-t border-gray-200">
              <div className="w-24 lg:w-32 mr-5 flex-shrink-0 flex items-center justify-center">
                <img src={globalExcellenceBanner} alt="GHP Global Excellence Awards 2025 Banner" className="max-h-10 lg:max-h-12 max-w-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mt-0 mb-0 heading-xs"><i>Best Oculoplastic Surgeon — London</i></h3>
                <p className="mt-1 mb-0 font-mono text-xs tracking-wider text-gold600 uppercase">GHP Global Excellence Awards</p>
              </div>
              <p className="mt-0 mb-0 text-2xl text-gray-300 font-bold flex-shrink-0 ml-4">2026</p>
            </div>
            {/* Doctify – Great Patient Experience 2026 */}
            <div className="flex items-center py-5 border-t border-gray-200">
              <div className="w-24 lg:w-32 mr-5 flex-shrink-0 flex items-center justify-center">
                <img src={doctifyBadge} alt="Doctify Great Patient Experience" className="max-h-10 lg:max-h-12 max-w-full object-contain rounded" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mt-0 mb-0 heading-xs"><i>Great Patient Experience</i></h3>
                <p className="mt-1 mb-0 font-mono text-xs tracking-wider text-gold600 uppercase">Award Winner</p>
              </div>
              <p className="mt-0 mb-0 text-2xl text-gray-300 font-bold flex-shrink-0 ml-4">2026</p>
            </div>
            {/* Doctify – Great Patient Experience 2025 */}
            <div className="flex items-center py-5 border-t border-gray-200">
              <div className="w-24 lg:w-32 mr-5 flex-shrink-0 flex items-center justify-center">
                <img src={doctifyBadge} alt="Doctify Great Patient Experience" className="max-h-10 lg:max-h-12 max-w-full object-contain rounded" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mt-0 mb-0 heading-xs"><i>Great Patient Experience</i></h3>
                <p className="mt-1 mb-0 font-mono text-xs tracking-wider text-gold600 uppercase">Award Winner</p>
              </div>
              <p className="mt-0 mb-0 text-2xl text-gray-300 font-bold flex-shrink-0 ml-4">2025</p>
            </div>
            {/* Aesthetics Awards – Best Surgical Result 2023 */}
            <div className="flex items-center py-5 border-t border-gray-200">
              <div className="w-24 lg:w-32 mr-5 flex-shrink-0 flex items-center justify-center">
                <img src={aestheticAwards} alt="Aesthetics Awards" className="max-h-10 lg:max-h-12 max-w-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mt-0 mb-0 heading-xs"><i>Best Surgical Result</i></h3>
                <p className="mt-1 mb-0 font-mono text-xs tracking-wider text-gold600 uppercase">Highly Commended</p>
              </div>
              <p className="mt-0 mb-0 text-2xl text-gray-300 font-bold flex-shrink-0 ml-4">2023</p>
            </div>
            {/* Aesthetics Awards – Best Surgical Result 2022 */}
            <div className="flex items-center py-5 border-t border-b border-gray-200">
              <div className="w-24 lg:w-32 mr-5 flex-shrink-0 flex items-center justify-center">
                <img src={aestheticAwards} alt="Aesthetics Awards" className="max-h-10 lg:max-h-12 max-w-full object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mt-0 mb-0 heading-xs"><i>Best Surgical Result</i></h3>
                <p className="mt-1 mb-0 font-mono text-xs tracking-wider text-gold600 uppercase">Commended</p>
              </div>
              <p className="mt-0 mb-0 text-2xl text-gray-300 font-bold flex-shrink-0 ml-4">2022</p>
            </div>
          </div>
        </div>
      </div>
      {/* Tatler Recognition */}
      <div className="bg-white2">
        <div className={`${containerClasses.container} py-12 md:py-20 lg:py-24`}>
          {/* Header (full width) */}
          <div className="col-span-4 md:col-span-8 lg:col-span-12 text-center mb-10 lg:mb-14">
            <p className="my-0 overline">Editorial Recognition</p>
            <h2 className="mt-0 mb-6 heading-md">
              A <i>Tatler</i> Top Doctor
            </h2>
            <img
              loading="lazy"
              src={tatler}
              alt="Tatler"
              className="h-7 lg:h-9 mx-auto"
            />
          </div>
          {/* Image */}
          <div className="col-span-4 md:col-span-8 lg:col-span-5 mb-8 lg:mb-0">
            <img
              loading="lazy"
              className="w-full"
              alt="Dr Elizabeth Hawkes, Tatler Top Doctor and Consultant Oculoplastic Surgeon"
              src={elizabethTatler}
            />
          </div>
          {/* Accolade list */}
          <div className="flex flex-col col-span-4 lg:justify-center md:col-span-8 lg:col-span-6 lg:col-start-7">
            {[
              {
                year: "2026",
                title: "Tatler Top Doctors",
                subtitle: "Aesthetic Industry's Top 50 Surgeons & Doctors",
              },
              {
                year: "2025",
                title: "Tatler Top Doctors",
                subtitle: "Aesthetic Industry's Top 50 Surgeons & Doctors",
              },
              {
                year: "2025",
                title: "Tatler Beauty & Cosmetic Surgery Guide",
                subtitle: "Top 4 UK Eye Surgeons",
              },
              {
                year: "2023",
                title: "Tatler Address Book",
                subtitle: "Featured Listing",
              },
              {
                year: "2022",
                title: "Tatler Beauty & Cosmetic Surgery Guide",
                subtitle: "Best New Practitioners in the UK",
              },
            ].map((item, idx, arr) => (
              <div
                key={`tatler-${idx}`}
                className={`py-4 border-t border-gray-200${
                  idx === arr.length - 1 ? " border-b" : ""
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <h3 className="mt-0 mb-0 heading-xs"><i>{item.title}</i></h3>
                  <p className="mt-0 mb-0 ml-3 text-sm text-gray-400 flex-shrink-0">{item.year}</p>
                </div>
                <p className="mt-1 mb-0 font-mono text-xs tracking-wider text-gold600 uppercase">
                  {item.subtitle}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* About Section */}
      <div className="bg-white2 text-blackNew">
        <div
          className={`${containerClasses.container} py-12 md:py-24 lg:py-20`}
        >
          {/* About Section: Text */}
          <div className="flex flex-col items-start col-span-4 lg:justify-center md:col-span-8 lg:col-span-5">
            <p className="mt-0 overline">Dr Hawkes FRCOphth MBBS BSc</p>
            <h2 className="mt-0 mb-6 heading-md">
              Meet <i>Our Surgeon</i>
            </h2>
            <p>
              Dr Hawkes Clinic is led by Dr Elizabeth Hawkes, Consultant
              Ophthalmic and Oculoplastic Surgeon. The clinic specialises in eye
              and eyelid surgery, focusing on{" "}
              <Link to="/oculoplastic-services/blepharoplasty-surgery/">
                blepharoplasty
              </Link>{" "}
              and facial aesthetics, with advanced techniques tailored to each
              patient&apos;s anatomy to achieve natural face and eye
              rejuvenation. For more insights into our work and techniques,
              visit the{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.youtube.com/@DrElizabethHawkes"
              >
                YouTube Channel
              </a>
              . Dr Hawkes Clinic is proud to be recognised through multiple
              industry awards and looks forward to welcoming you in
              consultation.
            </p>
            {/* About Section: Image */}
            <img
              loading="lazy"
              className="h-10 my-0"
              src={signature}
              alt="Mock Signature Dr Elizabeth Hawkes"
            />
            <Link
              className={`text-center w-full block mt-4 mb-8 lg:inline-block md:mr-1 lg:w-auto ${buttons.btnLargeExpressive}`}
              to="/about/"
            >
              <div className="flex items-center justify-between">
                Meet Dr Hawkes
                <ArrowRight className="ml-10" size="16" />
              </div>
            </Link>
          </div>
          <div className="col-span-4 md:col-span-8 lg:col-span-6 lg:col-start-7">
            <img
              loading="lazy"
              className="w-full"
              alt="Dr Hawkes wearing a red pleated suit with a white top and gold necklace, leaning against a white wall in a bright room."
              src={profile3}
            />
          </div>
        </div>
      </div>
      {/* Logo Quilt 2 */}
      <div className="bg-white2">
        <div className="col-span-4 pt-8 pb-4 md:col-span-8 lg:col-span-12">
          <p className="mb-0 font-mono text-xs text-center text-gray-500">
            Press & Media Features
          </p>
          <div className="marquee">
            <div className="flex items-center marquee-content">
              {[...pressLogos, ...pressLogos].map((logo, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center shrink-0 h-16 w-36 mx-6"
                  aria-hidden={i >= pressLogos.length ? "true" : undefined}
                >
                  <img
                    loading="lazy"
                    className={`w-auto max-w-full object-contain ${logo.maxH || "max-h-8"}`}
                    src={logo.src}
                    alt={`${logo.alt} Logo`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* In the Press */}
      <div className="bg-white md:hidden lg:block">
        <div
          className={`${containerClasses.container} py-10 md:py-24 lg:py-20`}
        >
          <p className="col-span-4 mb-0 md:col-span-8 lg:col-span-12 overline">
            Featured
          </p>
          <h2 className="col-span-4 mt-0 mb-1 lg:col-span-12 md:col-span-8 heading-md">
            In the Press
          </h2>
          <p className="col-span-4 mt-0 mb-6 text-gray-600 md:col-span-8 lg:col-span-8 lg:mb-8">
            Dr Hawkes is regularly featured in leading national publications and media as an expert in blepharoplasty, oculoplastic surgery and medical aesthetic treatments.
          </p>

          {/* First press piece */}
          <div className="relative flex col-span-4 mb-2 shadow-md lg:mb-0 lg:flex-grow lg:row-span-2 lg:flex lg:col-span-6">
            <a
              href="/in-the-press/featured/polly-vernon-eyelid-surgery-experience"
              className="block bg-white w-full"
            >
              <div className="bg-white flex flex-col h-full">
                <img
                  loading="lazy"
                  className="object-cover w-full lg:max-h-56 xl:max-h-64"
                  alt="Dr Hawkes wearing a black blouse with a pearl necklace, holding a book titled 'Tatler Address Book 2024' against a white background."
                  src={blogPlaceholder01}
                />
                <div className="flex flex-col flex-grow p-4 pt-6 pb-6 lg:p-8">
                  <p className="mb-1 font-mono text-xs text-gray-500 uppercase">
                    Nov 2024
                  </p>
                  <h2 className="mb-1 xl:mb-2 heading-sm">
                    Polly Vernon Eyelift Feature – The Sunday Times Style
                  </h2>
                  <p className="hidden text-gray-600 lg:block xl:mb-5">
                    Polly Vernon shares her experience of upper eyelid
                    blepharoplasty with Dr Elizabeth Hawkes, from early nerves
                    to results that exceeded expectations.
                  </p>
                  <p className="mt-auto mb-0 font-mono text-xs tracking-wider text-blackNew underline">
                    Read on-site &rarr;
                  </p>
                </div>
              </div>
            </a>
          </div>
          {/* Press cards 2-4 wrapper */}
          <div className="col-span-4 grid grid-cols-2 gap-4 lg:flex lg:flex-col lg:col-span-6 lg:col-start-7 lg:row-span-2">
            <div className="bg-white shadow-md mb-2 lg:mb-4 lg:flex-1">
              <a
                href="/in-the-press/featured/dr-elizabeth-hawkes-grazia-dark-circles"
                className="block h-full"
              >
                <div className="flex flex-col h-full lg:flex-row">
                  <img
                    loading="lazy"
                    className="object-cover w-full h-1/3 lg:w-1/2"
                    alt="Close-up of a woman's face focusing on her eyes and under-eye area, highlighting natural skin texture, with Grazia text overlay."
                    src="/assets/press-page-images-mf-grazia-03.jpg"
                  />
                  <div className="flex flex-col flex-grow p-4 pt-6 pb-6 lg:p-8">
                    <p className="mt-0 mb-0 font-mono text-xs leading-none text-gray-500 uppercase">
                      Nov 2024
                    </p>
                    <h2 className="mt-1 mb-2 heading-xs">
                      Dr Elizabeth Hawkes Featured in Grazia
                    </h2>
                    <p className="mt-auto mb-0 font-mono text-xs tracking-wider text-blackNew underline">
                      Read on-site &rarr;
                    </p>
                  </div>
                </div>
              </a>
            </div>
            <div className="bg-white shadow-md mb-2 lg:mb-4 lg:flex-1">
              <a
                href="https://www.harpersbazaar.com/uk/beauty/skincare/g70045632/best-hydrating-eye-creams-and-serums/"
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full"
              >
                <div className="flex flex-col h-full lg:flex-row">
                  <img
                    loading="lazy"
                    className="object-cover w-full h-1/3 lg:w-1/2"
                    alt="Stack of hydrating eye creams and serums displayed together, featured in Harper's Bazaar UK article about hydrating eye products"
                    src="/assets/press-page-images.jpg"
                  />
                  <div className="flex flex-col flex-grow p-4 pt-6 pb-6 lg:p-8">
                    <p className="mt-0 mb-0 font-mono text-xs leading-none text-gray-500 uppercase">
                      Jan 2026
                    </p>
                    <h2 className="mt-1 mb-2 heading-xs">
                      Top Hydrating Eye Creams and Serums Guide
                    </h2>
                    <p className="mt-auto mb-0 font-mono text-xs tracking-wider text-blackNew underline">
                      Read off-site &rarr;
                    </p>
                  </div>
                </div>
              </a>
            </div>
            <div className="flex-col justify-between flex-grow mb-2 bg-white shadow-md hidden lg:flex lg:flex-1 lg:w-full lg:flex-row lg:mb-0 lg:col-span-6">
              <a
                href="https://www.vogue.co.uk/article/how-to-get-rid-of-puffy-eyes"
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full"
              >
                <div className="flex flex-col h-full lg:flex-row">
                  <img
                    loading="lazy"
                    className="object-cover w-full h-1/3 lg:w-1/2 lg:h-auto"
                    alt="Close-up of a relaxed face with gentle under-eye care, featured in British Vogue article about reducing puffy eyes"
                    src="/assets/press-page-images-british-vogue.jpg"
                  />
                  <div className="flex flex-col flex-grow p-4 pt-6 pb-6 lg:p-8">
                    <p className="my-1 font-mono text-xs leading-none text-gray-500 uppercase">
                      Dec 2025
                    </p>
                    <h2 className="mt-1 mb-2 heading-xs">
                      Puffy Eyes Treatments
                    </h2>
                    <p className="mt-auto mb-0 font-mono text-xs tracking-wider text-blackNew underline">
                      Read off-site &rarr;
                    </p>
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* View all button */}
          <div className="col-span-4 md:col-span-8 lg:col-span-12 flex justify-center mt-8">
            <Link
              className="inline-block bg-blackNew text-white font-mono text-xs tracking-wider uppercase px-8 py-3 hover:bg-gold600 transition-colors duration-200"
              to="/in-the-press/"
            >
              View All Press
            </Link>
          </div>
        </div>
      </div>
      {/* Blog */}
      <div className="bg-white2 md:hidden lg:block">
        <div
          className={`${containerClasses.container} py-10 md:py-24 lg:py-20`}
        >
          <p className="col-span-4 mb-0 md:col-span-8 lg:col-span-12 overline">
            Featured
          </p>
          <h2 className="col-span-4 mt-0 mb-1 lg:col-span-12 md:col-span-8 heading-md">
            From the Blog
          </h2>
          <p className="col-span-4 mt-0 mb-6 text-gray-600 md:col-span-8 lg:col-span-8 lg:mb-8">
            Insights and advice from Dr Hawkes on blepharoplasty, oculoplastic surgery and medical aesthetic treatments.
          </p>

          {/* First blog post - Tatler */}
          <div className="relative flex col-span-4 mb-2 shadow-md lg:mb-0 lg:flex-grow lg:row-span-2 lg:flex lg:col-span-6">
            <a
              href="/blog/dr-hawkes-tatler-addition"
              className="block bg-white w-full"
            >
              <div className="bg-white flex flex-col h-full">
                <img
                  loading="lazy"
                  className="object-cover w-full lg:max-h-56 xl:max-h-64"
                  alt="Close-up of a person's eyebrow being measured with a white ruler for precision."
                  src={blogPlaceholder02}
                />
                <div className="flex flex-col flex-grow p-4 pt-6 pb-6 lg:p-8">
                  <p className="mb-1 font-mono text-xs text-gray-500 uppercase">
                    Company News
                  </p>
                  <h2 className="mb-1 xl:mb-2 heading-sm">
                    Dr Elizabeth Hawkes: The Latest Addition to Tatler&apos;s
                    Address Book!
                  </h2>
                  <p className="mt-auto mb-0 font-mono text-xs tracking-wider text-blackNew underline">
                    Read post &rarr;
                  </p>
                </div>
              </div>
            </a>
          </div>
          {/* Blog cards 2-4 wrapper */}
          <div className="col-span-4 grid grid-cols-2 gap-4 lg:flex lg:flex-col lg:col-span-6 lg:col-start-7 lg:row-span-2">
            <div className="bg-white shadow-md mb-2 lg:mb-4 lg:flex-1">
              <a
                href="/blog/the-secret-to-beautiful-eyes-ii:-dark-circles"
                className="block h-full"
              >
                <div className="flex flex-col h-full lg:flex-row">
                  <img
                    loading="lazy"
                    className="object-cover w-full h-1/3 lg:w-1/2"
                    alt="Woman with dark circles"
                    src="/assets/elizabeth-blog-03.jpg"
                  />
                  <div className="flex flex-col flex-grow p-4 pt-6 pb-6 lg:p-8">
                    <p className="mt-0 mb-0 font-mono text-xs leading-none text-gray-500 uppercase">
                      Eyes
                    </p>
                    <h2 className="mt-1 mb-2 heading-xs">
                      The Secret to Beautiful Eyes II: Dark Circles
                    </h2>
                    <p className="mt-auto mb-0 font-mono text-xs tracking-wider text-blackNew underline">
                      Read post &rarr;
                    </p>
                  </div>
                </div>
              </a>
            </div>
            <div className="bg-white shadow-md mb-2 lg:mb-4 lg:flex-1">
              <a
                href="/blog/the-secret-to-beautiful-eyes-iii-expert-solutions-for-under-eye-puffiness"
                className="block h-full"
              >
                <div className="flex flex-col h-full lg:flex-row">
                  <img
                    loading="lazy"
                    className="object-cover w-full h-1/3 lg:w-1/2"
                    alt="Woman touches eye and smiles in mirror"
                    src="/assets/elizabeth-blog-02.jpg"
                  />
                  <div className="flex flex-col flex-grow p-4 pt-6 pb-6 lg:p-8">
                    <p className="mt-0 mb-0 font-mono text-xs leading-none text-gray-500 uppercase">
                      Eyes
                    </p>
                    <h2 className="mt-1 mb-2 heading-xs">
                      The Secret to Beautiful Eyes III: Under-Eye Puffiness
                    </h2>
                    <p className="mt-auto mb-0 font-mono text-xs tracking-wider text-blackNew underline">
                      Read post &rarr;
                    </p>
                  </div>
                </div>
              </a>
            </div>
            <div className="flex-col justify-between flex-grow mb-2 bg-white shadow-md hidden lg:flex lg:flex-1 lg:w-full lg:flex-row lg:mb-0 lg:col-span-6">
              <a
                href="/blog/the-secret-to-beautiful-eyes-iv:-expert-tips-for-longer-lashes"
                className="block h-full"
              >
                <div className="flex flex-col h-full lg:flex-row">
                  <img
                    loading="lazy"
                    className="object-cover w-full h-1/3 lg:w-1/2 lg:h-auto"
                    alt="Woman applies mascara and smiles in mirror"
                    src="/assets/elizabeth-blog-04.jpg"
                  />
                  <div className="flex flex-col flex-grow p-4 pt-6 pb-6 lg:p-8">
                    <p className="mt-0 mb-0 font-mono text-xs leading-none text-gray-500 uppercase">
                      Eyes
                    </p>
                    <h2 className="mt-1 mb-2 heading-xs">
                      The Secret to Beautiful Eyes IV: Expert Tips for
                      Longer Lashes
                    </h2>
                    <p className="mt-auto mb-0 font-mono text-xs tracking-wider text-blackNew underline">
                      Read post &rarr;
                    </p>
                  </div>
                </div>
              </a>
            </div>
          </div>

          {/* View all button */}
          <div className="col-span-4 md:col-span-8 lg:col-span-12 flex justify-center mt-8">
            <Link
              className="inline-block bg-blackNew text-white font-mono text-xs tracking-wider uppercase px-8 py-3 hover:bg-gold600 transition-colors duration-200"
              to="/blog/"
            >
              View All Posts
            </Link>
          </div>
        </div>
      </div>
      {/* YouTube */}
      <div className="py-12 bg-white2 md:py-24 lg:py-20">
        <div className={`${containerClasses.container}`}>
          <div className="col-span-4 md:col-span-8 lg:col-span-10 lg:col-start-2">
            <p className="mb-0 text-center overline">Watch</p>
            <h2 className="mt-4 mb-8 text-center heading-md">
              Meet <i>Dr Hawkes</i>
            </h2>
            <div
              className="relative w-full"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                className="absolute inset-0 w-full h-full"
                src="https://www.youtube.com/embed/O_gSPj2enDI?si=Ed80cn9H9pkLmC9G"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </div>
      {/* Instagram Feed */}
      <div className="py-12 bg-white md:py-24 lg:py-20">
        <div
          className={`${containerClasses.container}`}
        >
          <div className="col-span-4 md:col-span-8 lg:col-span-10 lg:col-start-2">
            <p className="mb-0 text-center overline">Follow Dr Hawkes Clinic</p>
            <h2 className="mt-4 mb-8 text-center heading-md">
              Latest on Instagram
            </h2>
            <InstagramFeed limit={9} />
          </div>
        </div>
      </div>
      <CTABlock />
      <AffiliateList />
    </Layout>
  )
}

export default IndexPage
